import { PASSWORD_MIN, passwordProblems, type Session } from '@cyberscore/contracts';
import {
  BadgeCheck,
  CircleAlert,
  CircleCheck,
  Eye,
  EyeOff,
  Gauge,
  Info,
  MailCheck,
  Route as RouteIcon,
  Share2,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { accounts, issueFor } from '../../lib/auth';
import { href, navigate } from '../../lib/router';
import { useSession } from '../../lib/session';
import '../business/business.css';
import './auth.css';

/** Sign-in, sign-up and the email links (decision 0033): the first thing a new company sees. */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="biz auth">
      <aside className="auth-aside">
        <a className="biz-brand" href={href.signIn()}>
          <span className="biz-mark">
            <ShieldCheck size={19} />
          </span>
          <span>
            CyberScore<small>CyberCheck</small>
          </span>
        </a>
        <h2>Know how well your business is protected.</h2>
        <ul>
          <li>
            <Gauge size={18} /> Answer the Cyber Check in about 30 minutes, in a form or in chat, and get your
            CyberScore.
          </li>
          <li>
            <RouteIcon size={18} /> See what to fix first, with a 90-day plan sized for a small business.
          </li>
          <li>
            <Share2 size={18} /> Share your score with insurers, customers and suppliers when you choose.
          </li>
        </ul>
        <p className="fine">Your answers stay private to your business and the people you invite.</p>
      </aside>
      <main className="auth-main">
        <div className="auth-card">{children}</div>
      </main>
    </div>
  );
}

export function Field({
  label,
  error,
  aside,
  children,
}: {
  label: string;
  error?: string | null;
  aside?: ReactNode;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="auth-field">
      <div className="row-label">
        <label htmlFor={id}>{label}</label>
        {aside}
      </div>
      {children(id)}
      {error && <div className="auth-err">{error}</div>}
    </div>
  );
}

function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  invalid,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  invalid?: boolean;
}) {
  // prettier-ignore
  const [shown, setShown] = useState(false);
  return (
    <div className="auth-reveal">
      <input
        id={id}
        className="auth-input"
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        aria-invalid={invalid || undefined}
        required
      />
      <button
        type="button"
        aria-label={shown ? 'Hide password' : 'Show password'}
        onClick={() => setShown((s) => !s)}
      >
        {shown ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
}

/** The password rules, ticked as they are met. */
export function PasswordRules({
  password,
  email,
  name,
}: {
  password: string;
  email?: string;
  name?: string;
}) {
  const problems = passwordProblems(password, { email, name });
  const rules = [
    { ok: password.length >= PASSWORD_MIN, text: `At least ${PASSWORD_MIN} characters` },
    { ok: password.length > 0 && !problems.some((p) => p.startsWith('Avoid') || p.startsWith('Use more')), text: 'Not a common password' }, // prettier-ignore
    {
      ok: password.length > 0 && !problems.some((p) => p.startsWith('Don’t')),
      text: 'Not your name or email',
    },
  ];
  return (
    <ul className="rules" aria-label="Password rules">
      {rules.map((r) => (
        <li key={r.text} className={r.ok ? 'ok' : undefined}>
          {r.ok ? <CircleCheck size={13} /> : <span aria-hidden>•</span>} {r.text}
        </li>
      ))}
    </ul>
  );
}

export function Alert({ tone = 'bad', children }: { tone?: 'bad' | 'ok' | 'info'; children: ReactNode }) {
  return (
    <div className={`auth-alert ${tone}`} role={tone === 'bad' ? 'alert' : 'status'}>
      {tone === 'ok' ? (
        <CircleCheck size={16} />
      ) : tone === 'info' ? (
        <Info size={16} />
      ) : (
        <CircleAlert size={16} />
      )}
      <div>{children}</div>
    </div>
  );
}

/** What went wrong, in words: the first specific reason the API gave, rather than "Invalid request". */
export const message = (e: unknown) =>
  e instanceof ApiError && e.issues.length
    ? e.issues[0]!.message
    : e instanceof Error
      ? e.message
      : 'Something went wrong. Try again.';

// ---------- Sign in ----------

export function SignInPage() {
  const { signedIn } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState<string | null>(null);

  if (ticket) return <SecondStepForm ticket={ticket} onBack={() => setTicket(null)} />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await accounts.signIn({ email, password });
      if ('mfa_required' in r) setTicket(r.ticket);
      else signedIn(r);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const unverified = error instanceof ApiError && error.code === 'email_unverified';

  return (
    <AuthLayout>
      <h1>Sign in</h1>
      <p className="lead">Welcome back. Sign in to see your CyberScore.</p>
      <form className="auth-form" onSubmit={submit} noValidate>
        {error != null && (unverified ? <Alert tone="info">{message(error)}</Alert> : <Alert>{message(error)}</Alert>)}
        <Field label="Email">
          {(id) => <input id={id} className="auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />}
        </Field>
        <Field label="Password" aside={<a className="link small" href={href.forgot()}>Forgot your password?</a>}>
          {(id) => <PasswordInput id={id} value={password} onChange={setPassword} autoComplete="current-password" />}
        </Field>
        <button type="submit" className="bbtn pri auth-submit" disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="auth-alt">
        New to CyberScore? <a className="link" href={href.signUp()}>Create an account</a>
      </p>
      {import.meta.env.DEV && <DevSignIn />}
    </AuthLayout>
  ); // prettier-ignore
}

function SecondStepForm({ ticket, onBack }: { ticket: string; onBack: () => void }) {
  const { signedIn } = useSession();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      signedIn(await accounts.secondStep(recovery ? { ticket, recovery_code: code } : { ticket, code }));
    } catch (err) {
      setError(err);
      if (err instanceof ApiError && err.code === 'ticket_expired') onBack();
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <h1>Two-step sign-in</h1>
      <p className="lead">
        {recovery
          ? 'Enter one of the recovery codes you saved when you turned on two-step sign-in. Each works once.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      <form className="auth-form" onSubmit={submit} noValidate>
        {error != null && <Alert>{message(error)}</Alert>}
        <Field label={recovery ? 'Recovery code' : 'Code'} error={issueFor(error, 'code')}>
          {(id) =>
            recovery ? (
              <input id={id} className="auth-input code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" placeholder="ABCD-EFGH-JK" autoFocus />
            ) : (
              <input id={id} className="auth-input code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" autoFocus />
            )
          }
        </Field>
        <button type="submit" className="bbtn pri auth-submit" disabled={busy || code.length < 6}>
          {busy ? 'Checking…' : 'Continue'}
        </button>
      </form>
      <p className="auth-alt">
        <button type="button" className="link" onClick={() => { setRecovery((r) => !r); setCode(''); setError(null); }}>
          {recovery ? 'Use a code from the app' : 'Use a recovery code instead'}
        </button>
        {' · '}
        <button type="button" className="link" onClick={onBack}>Start again</button>
      </p>
    </AuthLayout>
  ); // prettier-ignore
}

/** The seeded development users (apps/core/src/dev/seed.ts). Development builds only. */
const DEV_USERS = [
  { subject: 'dev-platform', name: 'Platform admin', note: 'AI providers and platform configuration' },
  { subject: 'dev-admin', name: 'Insurer admin', note: 'Selatan General Insurance (demo)' },
  { subject: 'dev-approver', name: 'Insurer approver', note: 'Approves configuration releases' },
  { subject: 'dev-sme', name: 'SME owner', note: 'Kinta Precision Works (demo)' },
];

function DevSignIn() {
  const { signInDev } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <details className="dev-signin" open>
      <summary>Development: sign in as a seeded user</summary>
      <div className="list">
        {DEV_USERS.map((u) => (
          <button
            key={u.subject}
            type="button"
            className="bbtn"
            disabled={!!busy}
            onClick={async () => {
              setBusy(u.subject);
              setError(null);
              try {
                await signInDev(u.subject);
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(null);
              }
            }}
          >
            <span>
              <b>{u.name}</b>
              <span className="muted small" style={{ display: 'block', fontWeight: 400 }}>
                {u.note}
              </span>
            </span>
            <span className="muted small mono">{u.subject}</span>
          </button>
        ))}
      </div>
      {error && <div className="auth-err">{error}</div>}
    </details>
  );
}

// ---------- Sign up ----------

export function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  if (sent) return <CheckEmail email={sent} onRestart={() => setSent(null)} />;

  const weak = passwordProblems(password, { email, name }).length > 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await accounts.signUp({ name, email, password });
      setSent(email.trim().toLowerCase());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout>
      <h1>Create your account</h1>
      <p className="lead">Free to start. You’ll set up your business next, then answer the Cyber Check.</p>
      <form className="auth-form" onSubmit={submit} noValidate>
        {error != null && !issueFor(error, 'password') && !issueFor(error, 'email') && <Alert>{message(error)}</Alert>}
        <Field label="Your name" error={issueFor(error, 'name')}>
          {(id) => <input id={id} className="auth-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required autoFocus />}
        </Field>
        <Field label="Work email" error={issueFor(error, 'email')}>
          {(id) => <input id={id} className="auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />}
        </Field>
        <Field label="Password" error={issueFor(error, 'password')}>
          {(id) => (
            <>
              <PasswordInput id={id} value={password} onChange={setPassword} autoComplete="new-password" invalid={!!issueFor(error, 'password')} />
              <PasswordRules password={password} email={email} name={name} />
            </>
          )}
        </Field>
        <button type="submit" className="bbtn pri auth-submit" disabled={busy || !name || !email || weak}>
          {busy ? 'Creating your account…' : 'Create account'}
        </button>
      </form>
      <p className="auth-alt">
        Already have an account? <a className="link" href={href.signIn()}>Sign in</a>
      </p>
    </AuthLayout>
  ); // prettier-ignore
}

function CheckEmail({ email, onRestart }: { email: string; onRestart: () => void }) {
  const [note, setNote] = useState<string | null>(null);
  return (
    <AuthLayout>
      <span className="hicon big">
        <MailCheck size={24} />
      </span>
      <h1 style={{ marginTop: 16 }}>Check your email</h1>
      <p className="lead">
        We sent a link to <b>{email}</b>. Open it on this device to confirm your address and sign in. It works for 24
        hours.
      </p>
      {note && <Alert tone="ok">{note}</Alert>}
      <p className="auth-alt">
        No email after a few minutes? Check spam, or{' '}
        <button type="button" className="link" onClick={async () => setNote((await accounts.resend(email).catch((e: Error) => ({ message: e.message }))).message)}>
          send it again
        </button>
        .{' '}
        <button type="button" className="link" onClick={onRestart}>Use another address</button>
      </p>
      {import.meta.env.DEV && (
        <p className="muted small" style={{ marginTop: 18 }}>
          Development: emails are printed by the API and kept at <a className="link" href={`/api/dev/mailbox?to=${encodeURIComponent(email)}`} target="_blank" rel="noreferrer">/api/dev/mailbox</a>.
        </p>
      )}
    </AuthLayout>
  ); // prettier-ignore
}

// ---------- Links from emails ----------

/** The same link opened twice (React's development double effects, a double click) is sent once. */
const pending = new Map<string, Promise<Session>>();
const once = (key: string, fn: () => Promise<Session>) => {
  if (!pending.has(key)) pending.set(key, fn());
  return pending.get(key)!;
};

export function VerifyPage({ token }: { token: string }) {
  const { signedIn } = useSession();
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    let live = true;
    once(`verify:${token}`, () => accounts.verify(token))
      .then((s) => {
        if (!live) return;
        signedIn(s);
        navigate('#/', { replace: true });
      })
      .catch((e) => live && setError(e));
    return () => {
      live = false;
    };
  }, [token, signedIn]);
  return (
    <AuthLayout>
      {error == null ? (
        <>
          <span className="hicon big"><BadgeCheck size={24} /></span>
          <h1 style={{ marginTop: 16 }}>Confirming your email…</h1>
        </>
      ) : (
        <>
          <h1>This link didn’t work</h1>
          <p className="lead">{message(error)}</p>
          <ResendForm />
          <p className="auth-alt">
            Already confirmed? <a className="link" href={href.signIn()}>Sign in</a>
          </p>
        </>
      )}
    </AuthLayout>
  ); // prettier-ignore
}

function ResendForm() {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState<string | null>(null);
  return (
    <form
      className="auth-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setNote((await accounts.resend(email).catch((err: Error) => ({ message: err.message }))).message);
      }}
    >
      {note && <Alert tone="ok">{note}</Alert>}
      <Field label="Email">
        {(id) => <input id={id} className="auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}
      </Field>
      <button type="submit" className="bbtn auth-submit" disabled={!email}>Send a new link</button>
    </form>
  ); // prettier-ignore
}

export function ForgotPage() {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout>
      <h1>Reset your password</h1>
      <p className="lead">Enter your email. If it has an account, we’ll send a link to choose a new password.</p>
      <form
        className="auth-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setNote((await accounts.forgot(email).catch((err: Error) => ({ message: err.message }))).message);
          setBusy(false);
        }}
      >
        {note && <Alert tone="ok">{note}</Alert>}
        <Field label="Email">
          {(id) => <input id={id} className="auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />}
        </Field>
        <button type="submit" className="bbtn pri auth-submit" disabled={busy || !email}>
          {busy ? 'Sending…' : 'Send the link'}
        </button>
      </form>
      <p className="auth-alt">
        <a className="link" href={href.signIn()}>Back to sign in</a>
      </p>
    </AuthLayout>
  ); // prettier-ignore
}

export function ResetPage({ token }: { token: string }) {
  const { signedIn } = useSession();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const weak = passwordProblems(password).length > 0;
  return (
    <AuthLayout>
      <h1>Choose a new password</h1>
      <p className="lead">This signs you out on every other device.</p>
      <form
        className="auth-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            signedIn(await accounts.reset({ token, password }));
            navigate('#/', { replace: true });
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
        noValidate
      >
        {error != null && !issueFor(error, 'password') && <Alert>{message(error)}</Alert>}
        <Field label="New password" error={issueFor(error, 'password')}>
          {(id) => (
            <>
              <PasswordInput id={id} value={password} onChange={setPassword} autoComplete="new-password" />
              <PasswordRules password={password} />
            </>
          )}
        </Field>
        <button type="submit" className="bbtn pri auth-submit" disabled={busy || weak}>
          {busy ? 'Saving…' : 'Save and sign in'}
        </button>
      </form>
      {error instanceof ApiError && error.code === 'invalid_link' && (
        <p className="auth-alt">
          <a className="link" href={href.forgot()}>Ask for a new link</a>
        </p>
      )}
    </AuthLayout>
  ); // prettier-ignore
}
